import { Component, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import { LoginProvider } from '../../auth/dto/login.dto';
import { FirebaseErrorCode, isFirebaseError } from '../../error/error';
import { FormHelper } from '../../form/form-helper';
import { FormHelperFactory } from '../../form/form-helper.factory';
import { routes } from '../../routes/routes';
import { SnackbarService } from '../../service/snackbar.service';

const IN_APP_BROWSER_UA = /instagram|fbav|fban|twitter|pinterest|snapchat|linkedin|whatsapp|micromessenger|viber|slack/i;

export interface EmailProviderForm {
  email: FormControl<string>;
}

export interface PasswordProviderForm {
  email: FormControl<string>;
  password: FormControl<string>;
}

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
})
export class LoginPageComponent implements OnInit {
  emailForm: FormGroup<EmailProviderForm>;
  emailFormHelper: FormHelper<EmailProviderForm>;

  passwordForm: FormGroup<PasswordProviderForm>;
  passwordFormHelper: FormHelper<PasswordProviderForm>;

  passwordLoginEnabled = environment.enablePasswordLogin;

  wantsToLoginWithEmail = signal(false);
  wantsToLoginWithPassword = signal(false);
  showLoginForm = signal(false);
  isLoggingIn = signal(false);
  isUsingInAppBrowser = signal(false);

  protected readonly routes = routes;

  constructor(
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private snackBar: SnackbarService,
    private translateService: TranslateService,
    formHelperFactory: FormHelperFactory,
  ) {
    this.activatedRoute.queryParamMap.subscribe(map => {
      if (map.has('loginWithPassword')) {
        this.passwordLoginEnabled = true;
      }
    });

    const fb = new FormBuilder();
    this.emailForm = fb.group(
      {
        email: fb.nonNullable.control('', [Validators.required, Validators.email]),
      },
      { updateOn: 'submit' },
    );
    this.emailFormHelper = formHelperFactory.create(this.emailForm);

    this.passwordForm = fb.group(
      {
        email: fb.nonNullable.control('', [Validators.required, Validators.email]),
        password: fb.nonNullable.control('', [Validators.required]),
      },
      { updateOn: 'submit' },
    );
    this.passwordFormHelper = formHelperFactory.create(this.passwordForm);

    // Redirect after successful login or show error message in case of errors.
    authService.currentUser$.pipe(takeUntilDestroyed()).subscribe({
      next: user => {
        if (user) {
          this.router.navigate([user.redirectUrlAfterLogin], { queryParamsHandling: 'merge' });
        }
      },
      error: () => {
        this.snackBar.showError(this.translateService.instant('PAGE.LOGIN.NOTIFICATION.LOGIN_FAILED'));
        this.showLoginForm.set(true);
      },
    });
  }

  async ngOnInit() {
    // Check if it is a request to sign in the user via redirect from Google or Facebook.
    try {
      await this.authService.handleRedirectResultFromExternalProvider();
    } catch (e) {
      this.snackBar.showError(this.translateService.instant('PAGE.LOGIN.NOTIFICATION.LOGIN_FAILED'));
    } finally {
      this.showLoginForm.set(true);
    }

    // Check in-app browser usage
    this.isUsingInAppBrowser.set(this.isUserAgentInAppBrowser());
  }

  async loginWithProvider(provider: LoginProvider, event: MouseEvent) {
    event.preventDefault();
    await this.authService.login(provider);
  }

  async loginWithEmail() {
    this.emailFormHelper.trackSubmitted();
    if (this.emailForm.invalid) {
      return;
    }

    try {
      this.isLoggingIn.set(true);
      await this.authService.login({ type: 'email', email: this.emailForm.controls.email.value });
      this.wantsToLoginWithEmail.set(false);
      this.snackBar.showSuccess(this.translateService.instant('PAGE.LOGIN.NOTIFICATION.LOGIN_LINK_SENT'));
    } catch (e) {
      this.snackBar.showError(this.translateService.instant('PAGE.LOGIN.NOTIFICATION.SENDING_LOGIN_LINK_FAILED'));
    } finally {
      this.isLoggingIn.set(false);
    }
  }

  async loginWithPassword() {
    if (!this.passwordLoginEnabled) {
      return;
    }

    this.passwordFormHelper.trackSubmitted();
    if (this.passwordForm.invalid) {
      return;
    }

    try {
      this.isLoggingIn.set(true);
      await this.authService.login({
        type: 'password',
        email: this.passwordForm.controls.email.value,
        password: this.passwordForm.controls.password.value,
      });
      this.wantsToLoginWithPassword.set(false);
    } catch (e) {
      if (isFirebaseError(e)) {
        const message = this.errorMessageFromFirebaseError(e.code as FirebaseErrorCode);
        this.snackBar.showError(this.translateService.instant(message));
      } else {
        this.snackBar.showError(this.translateService.instant('PAGE.LOGIN.NOTIFICATION.LOGIN_FAILED'));
      }
    } finally {
      this.isLoggingIn.set(false);
    }
  }

  continueWithEmail(event: MouseEvent) {
    event.preventDefault();
    this.wantsToLoginWithEmail.set(true);
    this.wantsToLoginWithPassword.set(false);
  }

  continueWithPassword(event: MouseEvent) {
    event.preventDefault();
    this.wantsToLoginWithPassword.set(true);
    this.wantsToLoginWithEmail.set(false);
  }

  private errorMessageFromFirebaseError(code: FirebaseErrorCode): string {
    switch (code) {
      case 'auth/user-not-found':
        return this.translateService.instant('PAGE.LOGIN.NOTIFICATION.USER_NOT_FOUND');
      case 'auth/wrong-password':
        return this.translateService.instant('PAGE.LOGIN.NOTIFICATION.WRONG_PASSWORD');
      default:
        return this.translateService.instant('PAGE.LOGIN.NOTIFICATION.LOGIN_FAILED');
    }
  }

  private isUserAgentInAppBrowser(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;
    return IN_APP_BROWSER_UA.test(userAgent);
  }
}
